import {
	GetRetrospectiveApiResponse,
	MultiPlaceRetrospectiveData,
	PlaceInfo,
	RetrospectiveApiBigNumbers,
	RetrospectiveData,
} from "./types";
import { formatMonthLabel, safePercentage } from "./utils";
import { selectBestCuriosities } from "./curiosities";
import {
	aggregateBigNumbers,
	aggregateBigNumbersByType,
	aggregateProducts,
} from "./aggregation";

export function transformApiResponseToRetrospectiveData(
	apiResponse: GetRetrospectiveApiResponse,
	bigNumbers?: RetrospectiveApiBigNumbers,
): RetrospectiveData {
	const selectedBigNumbers =
		bigNumbers || apiResponse?.bigNumbers?.[0] || ({} as RetrospectiveApiBigNumbers);

	const tipoOperacao = selectedBigNumbers.tipo_operacao;
	const isCashless = tipoOperacao === "cashless";
	const isFicha = tipoOperacao === "ficha";
	const isMixed = tipoOperacao === "mixed";
	const qtdPessoas = selectedBigNumbers.qtd_pessoas || 0;
	const qtdProdutos = selectedBigNumbers.qtd_produtos || 0;
	const qtdPessoas24 = selectedBigNumbers.qtd_pessoas_24 || 0;
	const qtdProdutos24 = selectedBigNumbers.qtd_produtos_24 || 0;
	const checkins = selectedBigNumbers.checkins || 0;
	const checkins24 = selectedBigNumbers.checkins_24 || 0;
	const qtdTransacoes = selectedBigNumbers.qtd_transacoes || 0;
	const qtdTransacoes24 = selectedBigNumbers.qtd_transacoes_24 || 0;
	const tktMedioPessoas = selectedBigNumbers.tkt_medio_pessoas || 0;
	const tktMedioPessoas24 = selectedBigNumbers.tkt_medio_pessoas_24 || 0;
	const qtdFaturamento = selectedBigNumbers.faturamento || 0;
	const qtdFaturamento24 = selectedBigNumbers.faturamento_24 || 0;
	const qtdFaturamentoHorarioPico = selectedBigNumbers.qtd_faturamento_horario_pico || 0;
	const qtdFaturamentoDiaSemana = selectedBigNumbers.qtd_faturamento_dia_semana || 0;
	const qtdFaturamentoMelhorProduto =
		selectedBigNumbers.qtd_faturamento_melhor_produto || 0;

	// Calcular ticket médio por check-in (cashless) e por transação (ficha)
	const tktMedioPorCheckin = checkins > 0 ? qtdFaturamento / checkins : 0;
	const tktMedioPorTransacao = qtdTransacoes > 0 ? qtdFaturamento / qtdTransacoes : 0;
	const tktMedioPorTransacao24 =
		qtdTransacoes24 > 0 ? qtdFaturamento24 / qtdTransacoes24 : 0;

	const dias_operacao = selectedBigNumbers.dias_operacao || 0;
	const tempo_operacao_total_seg = selectedBigNumbers.tempo_operacao_total_seg || 0;

	// Calcular o total uma única vez para garantir consistência entre as seções
	// Para mixed, usar pessoas como padrão (será sobrescrito depois)
	const totalValue = isCashless || isMixed ? qtdPessoas : qtdProdutos;

	const monthlyEvolution = (apiResponse?.invoicing || []).reduce((acc, item) => {
		const monthLabel = formatMonthLabel(item.mes);
		const invoicing = item.faturamento;

		// Cashless: faturamento / qtd_pessoas
		// Ficha: faturamento / qtd_transacoes
		const divisor = isCashless ? item.qtd_pessoas : item.qtd_transacoes;

		// Se já existe dados para este mês, somar (múltiplos places)
		if (acc[monthLabel]) {
			acc[monthLabel].invoicing += invoicing;
			acc[monthLabel].totalDivisor += divisor;
			// Recalcular ticket médio com os valores somados
			acc[monthLabel].averageTicket =
				acc[monthLabel].totalDivisor > 0
					? acc[monthLabel].invoicing / acc[monthLabel].totalDivisor
					: 0;
		} else {
			acc[monthLabel] = {
				invoicing,
				averageTicket: divisor > 0 ? invoicing / divisor : 0,
				totalDivisor: divisor,
			};
		}

		return acc;
	}, {} as Record<string, { invoicing: number; averageTicket: number; totalDivisor: number }>);

	return {
		type: (isFicha ? "ficha" : isMixed ? "mixed" : "cashless") as
			| "cashless"
			| "ficha"
			| "mixed",
		summary: {
			total: totalValue,
		},
		highlights: {
			// Cashless: Total check-in e Ticket médio por check-in
			// Ficha: Quantidade de transações e Ticket médio por transação
			checkInsCompleted: isCashless ? checkins : qtdTransacoes,
			checkInsCompletedPercentageVsLastYear: isCashless
				? safePercentage(checkins, checkins24)
				: safePercentage(qtdTransacoes, qtdTransacoes24),
			averageTicket: isCashless ? tktMedioPorCheckin : tktMedioPorTransacao,
		},
		revelations: {
			//melhor dia da semana
			dayOfWeek: selectedBigNumbers.dia_semana_pico || "",
			percentageOfSalesDayOfWeek: safePercentage(qtdFaturamentoDiaSemana, qtdFaturamento),

			//horário de ouro
			timeRange: selectedBigNumbers.intervalo_horario || "",
			percentageOfSalesTimeRange: safePercentage(
				qtdFaturamentoHorarioPico,
				qtdFaturamento,
			),

			championProduct: {
				name: selectedBigNumbers.melhor_produto || "",
				percentage: safePercentage(qtdFaturamentoMelhorProduto, qtdFaturamento),
			},
		},
		invoicing: {
			monthlyEvolution,
			// Cashless: total de pessoas | Ficha: total de produtos vendidos
			total: totalValue,
			totalPercentageVsLastYear: isCashless
				? safePercentage(qtdPessoas, qtdPessoas24)
				: safePercentage(qtdProdutos, qtdProdutos24),
			// Cashless: ticket médio por pessoa | Ficha: ticket médio por transação
			averageTicket: isCashless ? tktMedioPessoas : tktMedioPorTransacao,
			averageTicketPercentageVsLastYear: isCashless
				? safePercentage(tktMedioPessoas, tktMedioPessoas24)
				: safePercentage(tktMedioPorTransacao, tktMedioPorTransacao24),
		},
		ranking: {
			drinks: (apiResponse?.products || [])
				.filter(p => p.categoria_produto === "bebida")
				.map(p => ({
					position: p.posicao_no_place,
					name: p.product_name,
					revenue: p.faturamento,
					quantity: p.qtd_produtos,
					unit: "unidades",
					percentage: safePercentage(
						p.faturamento,
						selectedBigNumbers.faturamento_bebidas || 0,
					),
				})),
			food: (apiResponse?.products || [])
				.filter(p => p.categoria_produto === "comida")
				.map(p => ({
					position: p.posicao_no_place,
					name: p.product_name,
					revenue: p.faturamento,
					quantity: p.qtd_produtos,
					unit: "unidades",
					percentage: safePercentage(
						p.faturamento,
						selectedBigNumbers.faturamento_comidas || 0,
					),
				})),
		},
		operatingTime: {
			daysOfOperation: dias_operacao,
			hoursWorked: Math.floor(tempo_operacao_total_seg / 60 / 60),
			minutesInYear: Math.floor(tempo_operacao_total_seg / 60),
			comparisons: {
				footballMatches: 0,
				tripsToJapan: 0,
			},
		},
		curiosities: selectBestCuriosities(
			isCashless ? qtdPessoas : qtdProdutos,
			isCashless ? "cashless" : "ficha",
			{
				daysOfOperation: dias_operacao,
				minutesInYear: Math.floor(tempo_operacao_total_seg / 60),
				hoursWorked: Math.floor(tempo_operacao_total_seg / 60 / 60),
			},
			selectedBigNumbers.qtd_litros_cervejas || 0,
		),
		qtd_litros_cervejas: selectedBigNumbers.qtd_litros_cervejas || 0,
	};
}

export function transformApiResponseToMultiPlaceData(
	apiResponse: GetRetrospectiveApiResponse,
): MultiPlaceRetrospectiveData {
	const bigNumbersArray = apiResponse?.bigNumbers || [];

	const places: PlaceInfo[] = bigNumbersArray.map(bn => ({
		id: bn.place_id,
		name: bn.place_name,
	}));

	const aggregatedBigNumbers = aggregateBigNumbers(bigNumbersArray);
	const isMixed = aggregatedBigNumbers.tipo_operacao === "mixed";

	let aggregatedData = transformApiResponseToRetrospectiveData(
		apiResponse,
		aggregatedBigNumbers,
	);

	aggregatedData.ranking = aggregateProducts(apiResponse);

	// Se for mixed, criar dados separados por tipo
	if (isMixed) {
		const cashlessBigNumbers = aggregateBigNumbersByType(bigNumbersArray, "cashless");
		const fichaBigNumbers = aggregateBigNumbersByType(bigNumbersArray, "ficha");

		const fichaBigNumbersForMixed = {
			...fichaBigNumbers,
			qtd_produtos: aggregatedBigNumbers.qtd_produtos,
			qtd_produtos_24: aggregatedBigNumbers.qtd_produtos_24,
			qtd_transacoes: aggregatedBigNumbers.qtd_transacoes,
			qtd_transacoes_24: aggregatedBigNumbers.qtd_transacoes_24,
			faturamento: aggregatedBigNumbers.faturamento,
			faturamento_24: aggregatedBigNumbers.faturamento_24,
		};

		// Criar responses separadas para cada tipo
		const cashlessResponse: GetRetrospectiveApiResponse = {
			bigNumbers: [cashlessBigNumbers],
			products: apiResponse.products.filter(
				p =>
					bigNumbersArray.find(bn => bn.place_id === p.place_id)?.tipo_operacao ===
					"cashless",
			),
			invoicing: apiResponse.invoicing.filter(
				i =>
					bigNumbersArray.find(bn => bn.place_id === i.place_id)?.tipo_operacao ===
					"cashless",
			),
		};

		const fichaResponse: GetRetrospectiveApiResponse = {
			bigNumbers: [fichaBigNumbersForMixed],
			products: apiResponse.products.filter(
				p =>
					bigNumbersArray.find(bn => bn.place_id === p.place_id)?.tipo_operacao ===
					"ficha",
			),
			invoicing: apiResponse.invoicing.filter(
				i =>
					bigNumbersArray.find(bn => bn.place_id === i.place_id)?.tipo_operacao ===
					"ficha",
			),
		};

		const cashlessData = transformApiResponseToRetrospectiveData(
			cashlessResponse,
			cashlessBigNumbers,
		);
		const fichaData = transformApiResponseToRetrospectiveData(
			fichaResponse,
			fichaBigNumbersForMixed,
		);

		aggregatedData.type = "mixed";
		aggregatedData.mixedData = {
			cashless: {
				summary: cashlessData.summary,
				highlights: cashlessData.highlights,
				invoicing: cashlessData.invoicing,
				curiosities: cashlessData.curiosities,
			},
			ficha: {
				summary: fichaData.summary,
				highlights: fichaData.highlights,
				invoicing: fichaData.invoicing,
				curiosities: fichaData.curiosities,
			},
		};
	}

	const byPlace: Record<string, RetrospectiveData> = {};
	bigNumbersArray.forEach(bn => {
		const placeSpecificResponse: GetRetrospectiveApiResponse = {
			bigNumbers: [bn],
			products: apiResponse.products.filter(p => p.place_id === bn.place_id),
			invoicing: apiResponse.invoicing.filter(i => i.place_id === bn.place_id),
		};
		byPlace[bn.place_id] = transformApiResponseToRetrospectiveData(
			placeSpecificResponse,
			bn,
		);
	});

	return {
		places: [{ id: "aggregated", name: "Todos os lugares" }, ...places],
		aggregated: aggregatedData,
		byPlace,
	};
}
